import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Upsell: handle clicks on upsell add button rendered in cart snippets
    this.addEventListener('click', this.#handleUpsellClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    this.removeEventListener('click', this.#handleUpsellClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Upsell: add variant via Ajax then trigger Horizon's cart refresh pipeline.
   * Button must have:
   *   data-cart-drawer-upsell-add
   *   data-variant-id="123"
   */
  #handleUpsellClick = async (event) => {
    const button = event.target?.closest?.('[data-cart-drawer-upsell-add]');
    if (!button) return;

    const variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const message = data?.description || data?.message || 'Add to cart failed';
        throw new Error(message);
      }

      // Important: Horizon often expects a CartAddEvent instance (not a plain CustomEvent)
      try {
        document.dispatchEvent(new CartAddEvent());
      } catch (_) {
        document.dispatchEvent(new CustomEvent(CartAddEvent.eventName));
      }

      // Give a visible success state even if drawer re-render is async
      button.textContent = 'Added';
      // Optional: keep disabled to prevent duplicates; or re-enable after a moment:
      setTimeout(() => {
        // If you want to allow adding multiple times, uncomment next line:
        // button.disabled = false;
        // button.textContent = originalText;
      }, 800);

      // Recalc sticky state after any updates
      requestAnimationFrame(() => this.#updateStickyState());
    } catch (err) {
      console.error(err);
      button.disabled = false;
      button.textContent = originalText;

      // Optional: show a simple alert so you actually see the reason
      // alert(err?.message || 'Could not add upsell');
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}